import React, { useState, useMemo } from 'react';
import { LifeBuoy, CheckCircle, AlertCircle, Calendar, MapPin, Users, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * DrillParticipationCard - Enhanced drill participation display
 * Shows compliance status, drill type breakdown, and participation history
 */
const DrillParticipationCard = ({ drillParticipation, drills, crewId, onViewHistory }) => {
  // Collapsed state for progressive disclosure
  const [isExpanded, setIsExpanded] = useState(false);

  // Get drills this crew member participated in
  const crewDrills = useMemo(() => {
    if (!drills || !crewId) return [];

    // Filter drills where crew member participated (using p.id, not p.crewId)
    const participatedDrills = drills.filter(drill =>
      drill.participants?.some(p => {
        // Handle both object format {id, name, position} and string format
        if (typeof p === 'object' && p !== null) {
          return p.id === crewId;
        }
        return p === crewId;
      })
    );

    // Sort by date (most recent first)
    return participatedDrills.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [drills, crewId]);

  // Calculate drill type breakdown
  const drillsByType = useMemo(() => {
    const types = {};

    crewDrills.forEach(drill => {
      const type = drill.type || 'Other';
      if (!types[type]) {
        types[type] = {
          count: 0,
          lastDate: null,
          drills: []
        };
      }
      types[type].count++;
      types[type].drills.push(drill);
      if (!types[type].lastDate || new Date(drill.date) > new Date(types[type].lastDate)) {
        types[type].lastDate = drill.date;
      }
    });

    return types;
  }, [crewDrills]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short'
    });
  };

  return (
    <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
      {/* Header - Inside Card */}
      <div className="bg-slate-700 px-5 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LifeBuoy className="h-5 w-5 text-white" />
            <h3 className="text-base font-semibold text-white">Drill Participation</h3>
          </div>
          {crewDrills.length > 0 && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800">
              {crewDrills.length} drill{crewDrills.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      <div className="px-6 py-5">
        {Object.keys(drillParticipation).length > 0 ? (
          <div className="space-y-4">
            {/* Compliance Status - Always visible */}
            <div className="flex items-start justify-between gap-2 pb-3 border-b border-gray-100">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">Compliance Status</p>
                <p className="text-xs text-gray-500 mt-1">
                  {(() => {
                    const overdue = Object.values(drillParticipation).filter(d => d.isOverdue).length;
                    const total = Object.keys(drillParticipation).length;
                    const compliant = total - overdue;
                    return `${compliant} of ${total} required drills compliant`;
                  })()}
                </p>
              </div>
              <div className="flex-shrink-0">
                {(() => {
                  const overdueCount = Object.values(drillParticipation).filter(d => d.isOverdue).length;
                  if (overdueCount === 0) {
                    return (
                      <span className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-green-600 bg-green-50 whitespace-nowrap">
                        <CheckCircle className="h-3 w-3 flex-shrink-0" />
                        <span>All Compliant</span>
                      </span>
                    );
                  } else {
                    return (
                      <span className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-red-600 bg-red-50 whitespace-nowrap">
                        <AlertCircle className="h-3 w-3 flex-shrink-0" />
                        <span>{overdueCount} Overdue</span>
                      </span>
                    );
                  }
                })()}
              </div>
            </div>

            {/* Expandable Content */}
            {isExpanded && (
              <>
                {/* Overdue Drills Summary */}
                {Object.entries(drillParticipation).filter(([_, data]) => data.isOverdue).length > 0 && (
                  <div className="bg-red-50 rounded-md p-3">
                    <p className="text-xs font-medium text-red-700 mb-2">Overdue Drills:</p>
                    <div className="space-y-1">
                      {Object.entries(drillParticipation)
                        .filter(([_, data]) => data.isOverdue)
                        .map(([drillType, data]) => (
                          <p key={drillType} className="text-xs text-red-600">
                            • {data.drillName} - {data.lastParticipation ? `${data.daysSince} days ago` : 'Never participated'}
                          </p>
                        ))}
                    </div>
                  </div>
                )}

                {/* Drill Type Breakdown */}
                {Object.keys(drillsByType).length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">By Drill Type</p>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(drillsByType).slice(0, 4).map(([type, data]) => (
                        <div key={type} className="bg-gray-50 rounded-md p-2">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium text-gray-900 truncate">{type}</span>
                            <span className="text-xs font-semibold text-cyan-600 bg-white px-1.5 py-0.5 rounded">
                              {data.count}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Last: {formatShortDate(data.lastDate)}
                          </p>
                        </div>
                      ))}
                    </div>
                    {Object.keys(drillsByType).length > 4 && (
                      <p className="text-xs text-gray-500 text-center">
                        +{Object.keys(drillsByType).length - 4} more types
                      </p>
                    )}
                  </div>
                )}

                {/* Recent Participation History */}
                {crewDrills.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Recent Participation</p>
                    <div className="space-y-2">
                      {crewDrills.slice(0, 3).map(drill => (
                        <div key={drill.id} className="flex items-start gap-3 py-2 px-2 bg-gray-50 rounded-md">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-cyan-100 text-cyan-700">
                                {drill.type || 'Drill'}
                              </span>
                              <span className={`text-xs px-1.5 py-0.5 rounded ${
                                drill.status === 'completed'
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-gray-100 text-gray-600'
                              }`}>
                                {drill.status || 'recorded'}
                              </span>
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {formatDate(drill.date)}
                              </span>
                              {drill.location && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="h-3 w-3" />
                                  {drill.location}
                                </span>
                              )}
                              {drill.participants?.length > 0 && (
                                <span className="flex items-center gap-1">
                                  <Users className="h-3 w-3" />
                                  {drill.participants.length}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      {crewDrills.length > 3 && (
                        <p className="text-xs text-gray-500 text-center">
                          +{crewDrills.length - 3} more drills
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* View History Button */}
                {onViewHistory && (
                  <button
                    onClick={onViewHistory}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-cyan-600 bg-white border border-cyan-600 rounded-md hover:bg-cyan-50 transition-colors"
                  >
                    View Full Drill History
                    <ChevronRight className="h-4 w-4" />
                  </button>
                )}
              </>
            )}

            {/* Expand/Collapse Toggle */}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="w-full flex items-center justify-center gap-1 py-2 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="h-4 w-4" />
                  Show less
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4" />
                  Show more
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="text-center py-4">
            <LifeBuoy className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 italic text-sm">No required drills configured</p>
            <p className="text-xs text-gray-400 mt-1">
              {crewDrills.length > 0
                ? `${crewDrills.length} drill${crewDrills.length !== 1 ? 's' : ''} recorded`
                : 'Drill records will appear here once configured'
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DrillParticipationCard;